import { formatDateTime } from "../UI/Utils/formatDate.ts";
import { FranchiseeItemFragment } from "../gql/graphql.ts";
import {
  CandidateWeeklyCapDto,
  ClassRoomSelectionHistoryEntryDto,
} from "./types.ts";
import { TeacherTierBadge } from "../Franchisee/TeacherAssignmentCap/TeacherTierBadge.tsx";

function strategyLabel(name: string | null): string {
  if (!name) return "No data available";
  const labels: Record<string, string> = {
    SelectInternalTeacherFirstStrategy: "Internal teacher",
    SelectNewTeacherStrategy: "New teacher",
    SelectTeacherBasedOnReviewsStrategy: "Best score",
    SelectBestTeacherWithinWeeklyCapStrategy: "Best score within weekly cap",
    SelectTeacherWithLessReviewStrategy: "Less reviewed",
    ManualAssignment: "Manually assigned",
    BulkCreation: "Bulk 1-on-1 creation",
    TeacherSwap: "Teacher swap",
  };
  return labels[name] ?? name;
}

function CandidateWeeklyCap({
  weeklyCap,
}: {
  weeklyCap: CandidateWeeklyCapDto;
}) {
  return (
    <span
      title={`${weeklyCap.assignedClassRooms} of ${weeklyCap.offeredSlots} weekly slots already assigned, cap ${weeklyCap.maxAssignableClassRooms} (${weeklyCap.maxPercentOfWeeklyAvailability}%)`}
      className={"flex items-center gap-1.5"}
    >
      <TeacherTierBadge tier={weeklyCap.tier} compact />
      {weeklyCap.hasBeenReached ? (
        <span
          className={"rounded bg-amber-100 px-1.5 font-medium text-amber-700"}
        >
          cap reached {weeklyCap.assignedClassRooms}/
          {weeklyCap.maxAssignableClassRooms}
        </span>
      ) : (
        <span>
          cap {weeklyCap.assignedClassRooms}/{weeklyCap.maxAssignableClassRooms}
        </span>
      )}
    </span>
  );
}

function CandidateRow({
  candidate,
  teacherById,
}: {
  candidate: ClassRoomSelectionHistoryEntryDto["candidates"][0];
  teacherById: Map<string, FranchiseeItemFragment>;
}) {
  const teacher = teacherById.get(candidate.teacherId);
  const displayName = teacher
    ? `${teacher.givenName} ${teacher.familyName}`
    : candidate.teacherId;
  return (
    <div
      className={[
        "flex items-center justify-between text-xs px-2 py-1 rounded",
        candidate.wasSelected
          ? "bg-green-50 border border-green-200"
          : "bg-slate-50",
      ].join(" ")}
    >
      <span
        className={
          candidate.wasSelected
            ? "font-semibold text-green-700"
            : "text-slate-600"
        }
      >
        {displayName}
        {candidate.wasSelected && (
          <span className={"ml-1 text-green-600"}>✓ selected</span>
        )}
      </span>
      <span
        className={
          "flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-slate-400 ml-2"
        }
      >
        <span>
          {candidate.reviewAvg != null
            ? `avg ${candidate.reviewAvg.toFixed(2)}`
            : "no score"}
          {candidate.reviewCount != null &&
            ` · ${candidate.reviewCount} reviews`}
        </span>
        {candidate.weeklyCap && (
          <CandidateWeeklyCap weeklyCap={candidate.weeklyCap} />
        )}
      </span>
    </div>
  );
}

export function ClassRoomSelectionHistory({
  history,
  teacherById,
}: {
  history: ClassRoomSelectionHistoryEntryDto[];
  teacherById: Map<string, FranchiseeItemFragment>;
}) {
  return (
    <div className={"pt-2 space-y-3"}>
      <p className={"text-xs text-slate-500 font-medium"}>
        Assignment history ({history.length}{" "}
        {history.length === 1 ? "entry" : "entries"})
      </p>
      <div className={"relative"}>
        {history.map((entry, index) => {
          const isLatest = index === history.length - 1;
          return (
            <div key={entry.assignedAt} className={"flex gap-2"}>
              <div className={"flex flex-col items-center"}>
                <div
                  className={[
                    "w-2.5 h-2.5 rounded-full mt-0.5 shrink-0",
                    isLatest ? "bg-primary" : "bg-slate-300",
                  ].join(" ")}
                />
                {index < history.length - 1 && (
                  <div className={"w-px flex-1 bg-slate-200 my-0.5"} />
                )}
              </div>
              <div className={"flex-1 pb-3"}>
                <div className={"flex items-baseline justify-between"}>
                  <span
                    className={[
                      "text-xs font-medium",
                      isLatest ? "text-slate-800" : "text-slate-500",
                    ].join(" ")}
                  >
                    {strategyLabel(entry.strategyName)}
                  </span>
                  <span className={"text-xs text-slate-400 ml-2 shrink-0"}>
                    {formatDateTime(entry.assignedAt)}
                  </span>
                </div>
                {entry.candidates.length > 0 && (
                  <div className={"space-y-1 mt-1"}>
                    {entry.candidates.map((c) => (
                      <CandidateRow
                        key={c.teacherId}
                        candidate={c}
                        teacherById={teacherById}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
